import React, { useState } from 'react'
import { Button, Col, Form, Modal, Row, Table } from 'react-bootstrap'
import { Info } from 'lucide-react'
import Select from 'react-select'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'

import CustomerLocation from '@data/locations/model/customerLocation'
import {
  useAccountLocationsQuery,
} from '@data/locations/accountLocations'
import { useGroupsQuery } from '@data/rbac/rbac'
import { useInviteUserMutation } from '@data/users/users'
import { ClientUserRole, Group } from '@data/users/model/user'

import { InviteLocation } from '@data/users/model/userManagement'

import {
  popoverAccountAdmin,
  popoverAutoAddAlerts,
  popoverAutoAddLocations,
  popoverLocationAdmin,
  popoverLocationAlerts,
  popoverEmergencyContact
} from './Popovers'

import { IModalProps } from './UserEditModal'
import { useAuthStore } from '@store/auth.store'

import './Modal.scss'
import { ClientPositionOptions } from '../constants'
import {
  hasPermissions,
  validateEmailStructure,
} from '@common/utils/validation/authValidators'

export const InviteModal: React.FC<IModalProps> = ({ show, account, onHide }: IModalProps) => {
  const { data: groups = [] } = useGroupsQuery()
  const { data: accountLocations = [] } = useAccountLocationsQuery(account?.id ?? null, show)
  const { mutate: inviteUser } = useInviteUserMutation()
  const permissions = useAuthStore((state) => state.permissions)

  const [email, setEmail] = useState('')
  const [selectedGroups, setSelectedGroups] = useState<Group[]>([])
  const [autoAddLocations, setAutoAddLocations] = useState(false)
  const [isAccountAdmin, setIsAccountAdmin] = useState(false)
  const [welcomeEmail, setWelcomeEmail] = useState(true)
  const [selectedLocations, setSelectedLocations] = useState<InviteLocation[]>([])
  const [selectAllLocations, setSelectAllLocations] = useState<boolean>(false)
  const [autoAddAlerts, setAutoAddAlerts] = useState(false)
  const [accountInvoice, setAccountInvoice] = useState(false)
  const [position, setPosition] = useState<ClientUserRole>('Other')
  // const [selectAllAdmin, setSelectAllAdmin] = useState<boolean>(false);
  // const [selectAllAlerts, setSelectAllAlerts] = useState<boolean>(false);
  const required = validateEmailStructure(email) && selectedLocations.length > 0

  const handleSave = () => {
    const updatedUser: any = {
      active: true,
      email,
    }

    // if no group is selected assign the Standard user permission
    const groupIds = selectedGroups.length > 0 ? selectedGroups.map((g) => g.id) : ['1']

    if (account) {
      inviteUser({
        user: updatedUser,
        groupIds,
        account,
        locations: selectedLocations,
        welcomeEmail,
        autoAddLocations,
        isAdmin: isAccountAdmin,
        autoAddAlerts,
        accountInvoice,
        position,
      })
    }
    onHide()
  }

  const handleLocations = (location: string, admin: boolean, alerts: boolean, emergencyContact: boolean) => {
    if (location === 'allLoc') {
      if (!selectAllLocations) {
        const locations: InviteLocation[] = accountLocations.map((l: CustomerLocation) => {
          return { locationId: l.id, admin: false, alerts: false, emergencyContact: false } as InviteLocation
        })
        setSelectedLocations(locations)
      } else {
        setSelectedLocations([])
      }
      setSelectAllLocations(!selectAllLocations)
      return
    } else {
      setSelectAllLocations(false)
    }

    const locationIndex = selectedLocations.findIndex((x) => x.locationId === location)
    if (locationIndex !== -1) {
      const locations = [...selectedLocations]
      if (admin) {
        locations[locationIndex] = {
          ...locations[locationIndex],
          admin: !locations[locationIndex].admin,
        }
      } else if (alerts) {
        locations[locationIndex] = {
          ...locations[locationIndex],
          alerts: !locations[locationIndex].alerts,
        }
      } else if (emergencyContact) {
        locations[locationIndex] = {
          ...locations[locationIndex],
          emergencyContact: !locations[locationIndex].emergencyContact,
        }
      } else {
        locations.splice(locationIndex, 1)
      }
      setSelectedLocations(locations)
    } else {
      setSelectedLocations([
        ...selectedLocations,
        { locationId: location, admin: false, alerts: false, emergencyContact: false },
      ])
    }
  }

  const handleAccountAdmin = (val: boolean) => {
    setIsAccountAdmin(val)
    if (!val) {
      setSelectedLocations([])
      setAutoAddLocations(false)
    } else {
      const locations: InviteLocation[] = accountLocations.map((l: CustomerLocation) => {
        return { locationId: l.id, admin: true, alerts: false } as InviteLocation
      })
      setSelectedLocations(locations)
      setAutoAddLocations(true)
    }
  }

  // noinspection RequiredAttributes
  return (
    <Modal
      show={show}
      onHide={onHide}
      backdrop='static'
      size='lg'
      aria-labelledby='contained-modal-title-vcenter'
      centered
    >
      <Modal.Header className='modal-header' closeButton>
        <Modal.Title id='contained-modal-title-vcenter'>Invite New User</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form className='invite-information-form'>
          <Row>
            <Form.Group as={Col} controlId='formGridName'>
              <Form.Label className={'fw-bold'}>Email</Form.Label>
              <Form.Control
                type='text'
                placeholder='Enter a Email'
                value={email}
                onChange={(e) => setEmail(e.target.value.trim())}
                isInvalid={!validateEmailStructure(email)}
              />
              <Form.Control.Feedback type='invalid'>Enter a valid email</Form.Control.Feedback>
            </Form.Group>
          </Row>
          <Row className={'mt-3'}>
            <Form.Group as={Col} controlId='accountOptions'>
              <Form.Label className={'fw-bold'}>Account Options</Form.Label>
              <div>
                <Form.Check
                  type='checkbox'
                  inline
                  // onChange={(e) => setIsAccountAdmin(!isAccountAdmin)}
                  onChange={() => handleAccountAdmin(!isAccountAdmin)}
                  checked={isAccountAdmin}
                  label={
                    <>
                      Account Admin
                      <OverlayTrigger
                        delay={{ hide: 450, show: 300 }}
                        trigger={['hover', 'focus']}
                        overlay={popoverAccountAdmin}
                        // containerPadding={20}
                        placement='bottom'
                      >
                        <Info size={15} className={'ml-1 text-info'} />
                      </OverlayTrigger>
                    </>
                  }
                  name='accountOptions'
                  id={`inline-account-options-1`}
                />
                <Form.Check
                  type='checkbox'
                  inline
                  onChange={() => setAccountInvoice(!accountInvoice)}
                  checked={accountInvoice}
                  label={
                    <>
                      Account Invoices
                      <OverlayTrigger
                        delay={{ hide: 450, show: 300 }}
                        trigger={['hover', 'focus']}
                        overlay={popoverAutoAddAlerts}
                        // containerPadding={20}
                        placement='bottom'
                      >
                        <Info size={15} className={'ml-1 text-info'} />
                      </OverlayTrigger>
                    </>
                  }
                  name='accountOptions'
                  id={`inline-account-options-2`}
                />
                <Form.Check
                  type='checkbox'
                  inline
                  onChange={() => setAutoAddLocations(!autoAddLocations)}
                  checked={autoAddLocations || isAccountAdmin}
                  disabled={isAccountAdmin}
                  label={
                    <>
                      Auto Add New Locations
                      <OverlayTrigger
                        delay={{ hide: 450, show: 300 }}
                        trigger={['hover', 'focus']}
                        overlay={popoverAutoAddLocations}
                        // containerPadding={20}
                        placement='bottom'
                      >
                        <Info size={15} className={'ml-1 text-info'} />
                      </OverlayTrigger>
                    </>
                  }
                  name='accountOptions'
                  id={`inline-account-options-3`}
                />
                <Form.Check
                  type='checkbox'
                  inline
                  onChange={() => setAutoAddAlerts(!autoAddAlerts)}
                  checked={autoAddAlerts}
                  label={
                    <>
                      Auto Add Alerts
                      <OverlayTrigger
                        delay={{ hide: 450, show: 300 }}
                        trigger={['hover', 'focus']}
                        overlay={popoverAutoAddAlerts}
                        // containerPadding={20}
                        placement='bottom'
                      >
                        <Info size={15} className={'ml-1 text-info'} />
                      </OverlayTrigger>
                    </>
                  }
                  name='accountOptions'
                  id={`inline-account-options-4`}
                />
              </div>
            </Form.Group>
          </Row>
          <Row className={'mt-3'}>
            <Form.Group as={Col} controlId='formGridPosition'>
              <Form.Label className={'fw-bold'}>Position</Form.Label>
              <Select
                classNamePrefix='select'
                menuPosition={'fixed'}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 2 }) }}
                value={{
                  value: position,
                  label: position
                }}
                onChange={(e: any) => setPosition(e.value)}
                options={
                  ClientPositionOptions.map((opt) => {
                    return { value: opt,  label: opt }
                  })
                }
                getOptionValue={(option) => option.value}
                getOptionLabel={(option) => `${option.label}`}
                isClearable={false}
                required
              />
            </Form.Group>
          </Row>
          {hasPermissions(permissions, ['CREATE:USER:INVITE', 'UPDATE:GROUPS']) && (
            <Row className={'mt-3'}>
              <Form.Group as={Col} controlId='formGridName'>
                <Form.Label className={'fw-bold'}>Groups</Form.Label>
                <Select
                  className='basic-multi-select'
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 2 }) }}
                  menuPosition={'fixed'}
                  value={selectedGroups}
                  onChange={(options: any) => setSelectedGroups([...options])}
                  options={groups}
                  isMulti
                  getOptionValue={(option) => option.name}
                  getOptionLabel={(option) => option.description}
                  isClearable={false}
                />
              </Form.Group>
            </Row>
          )}

          {account && (
            <Row className={'mt-3'}>
              <Form.Group as={Col} controlId='formGridLocations'>
                <Form.Label className={'fw-bold'}>Locations </Form.Label>
                <span
                  className='invalid-feedback fw-normal'
                  style={{display: selectedLocations.length < 1 ? 'block' : 'none'}}>Select a location
                </span>
                <div style={{ width: '100%', height: '300px', overflowY: 'auto' }}>
                  <Table striped>
                    <thead
                      style={{
                        width: '15%',
                        position: 'sticky',
                        top: -1,
                        zIndex: 1,
                      }}
                    >
                    <tr>
                      <th>Location Name</th>
                      <th className={'text-center'}>
                        Admin
                        <OverlayTrigger
                          delay={{ hide: 450, show: 300 }}
                          trigger={['hover', 'focus']}
                          overlay={popoverLocationAdmin}
                          // containerPadding={20}
                          placement='bottom'
                        >
                          <Info size={15} className={'ml-1 text-info'} />
                        </OverlayTrigger>
                      </th>
                      <th className={'text-center'}>
                        Alerts
                        <OverlayTrigger
                          delay={{ hide: 450, show: 300 }}
                          trigger={['hover', 'focus']}
                          overlay={popoverLocationAlerts}
                          // containerPadding={20}
                          placement='bottom'
                        >
                          <Info size={15} className={'ml-1 text-info'} />
                        </OverlayTrigger>
                      </th>
                      <th className={'text-center'}>
                        Emergency Contact
                        <OverlayTrigger
                          delay={{ hide: 450, show: 300 }}
                          trigger={['hover', 'focus']}
                          overlay={popoverEmergencyContact}
                          // containerPadding={20}
                          placement='bottom'
                        >
                          <Info size={15} className={'ml-1 text-info'} />
                        </OverlayTrigger>
                      </th>
                    </tr>
                    </thead>
                    <tbody>
                    {/*<tr key={'all'}>*/}
                    {/*  <td>*/}
                    {/*    <Form.Check*/}
                    {/*      type={'checkbox'}*/}
                    {/*      label={'All'}*/}
                    {/*      value={'allLoc'}*/}
                    {/*      id={`default-allLoc`}*/}
                    {/*      onChange={(e) => handleLocations(e.target.value, false, false)}*/}
                    {/*      className={'ml-2'}*/}
                    {/*      checked={selectAllLocations || selectedLocations.length === accountLocations.length}*/}
                    {/*      disabled={isAccountAdmin}*/}
                    {/*    />*/}
                    {/*  </td>*/}
                    {/*  <td>*/}
                    {/*    <Form.Check*/}
                    {/*      type={'checkbox'}*/}
                    {/*      label={'All'}*/}
                    {/*      value={'allAdmin'}*/}
                    {/*      id={`default-allLoc`}*/}
                    {/*      onChange={(e) => handleLocations(e.target.value, false, false)}*/}
                    {/*      className={'ml-2'}*/}
                    {/*      checked={selectAllAdmin}*/}
                    {/*      disabled={isAccountAdmin}*/}
                    {/*    />*/}
                    {/*  </td>*/}
                    {/*  <td>*/}
                    {/*    <Form.Check*/}
                    {/*      type={'checkbox'}*/}
                    {/*      label={'All'}*/}
                    {/*      value={'allAlerts'}*/}
                    {/*      id={`default-allLoc`}*/}
                    {/*      onChange={(e) => handleLocations(e.target.value, false, false)}*/}
                    {/*      className={'ml-2'}*/}
                    {/*      checked={selectAllAlerts}*/}
                    {/*      disabled={isAccountAdmin}*/}
                    {/*    />*/}
                    {/*  </td>*/}
                    {/*</tr>*/}
                    {accountLocations.map((l) => (
                      <tr key={l.id}>
                        <td>
                          <Form.Check
                            type={'checkbox'}
                            label={l.name}
                            value={l.id}
                            id={`default-${l.id}`}
                            onChange={(e) => handleLocations(e.target.value, false, false, false)}
                            className={'ml-2'}
                            checked={
                              isAccountAdmin ||
                              !!selectedLocations.find((loc) => loc.locationId === l.id)
                            }
                            disabled={isAccountAdmin}
                          />
                        </td>
                        <td className={'text-center'}>
                          <Form.Check
                            type='checkbox'
                            label={''}
                            value={l.id}
                            disabled={
                              !selectedLocations.find((loc) => loc.locationId === l.id) ||
                              isAccountAdmin
                            }
                            onChange={(e) => handleLocations(e.target.value, true, false, false)}
                            checked={
                              !!selectedLocations.find(
                                (loc) => loc.admin && loc.locationId === l.id,
                              )
                            }
                          />
                        </td>
                        <td className={'text-center'}>
                          <Form.Check
                            type='checkbox'
                            label={''}
                            value={l.id}
                            disabled={!selectedLocations.find((loc) => loc.locationId === l.id)}
                            onChange={(e) => handleLocations(e.target.value, false, true, false)}
                            checked={
                              !!selectedLocations.find(
                                (loc) => loc.alerts && loc.locationId === l.id,
                              )
                            }
                          />
                        </td>
                        <td className={'text-center'}>
                          <Form.Check
                            type='checkbox'
                            label={''}
                            value={l.id}
                            disabled={!selectedLocations.find((loc) => loc.locationId === l.id)}
                            onChange={(e) => handleLocations(e.target.value, false, false, true)}
                            checked={
                              !!selectedLocations.find(
                                (loc) => loc.emergencyContact && loc.locationId === l.id,
                              )
                            }
                          />
                        </td>
                      </tr>
                    ))}
                    </tbody>
                  </Table>
                </div>
              </Form.Group>
            </Row>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button className='btn btn-secondary' onClick={onHide}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={!required}
        >
          Invite User
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
